import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { NET, CHAIN_HEX, explorerTx } from '../config';
import { contract, errMsg, withGasHeadroom } from '../lib';

const Ctx = createContext(null);

// Injected wallets only (no WalletConnect). EIP-6963 announcements are preferred; legacy globals are the fallback.
export const WALLETS = [
  { id: 'binance', name: 'Binance Wallet', icon: '🟡', rdns: ['com.binance.wallet', 'com.binance'],
    install: 'https://www.binance.com/en/web3wallet',
    legacy: () => window.binancew3w?.ethereum || (window.ethereum?.isBinance ? window.ethereum : null) || window.BinanceChain || null },
  { id: 'metamask', name: 'MetaMask', icon: '🦊', rdns: ['io.metamask', 'io.metamask.flask'],
    install: 'https://metamask.io/download/',
    legacy: () => {
      const list = window.ethereum?.providers || (window.ethereum ? [window.ethereum] : []);
      return list.find((p) => p.isMetaMask && !p.isRabby && !p.isOkxWallet && !p.isBinance && !p.isBraveWallet) || null;
    } },
  { id: 'okx', name: 'OKX Wallet', icon: '⚫', rdns: ['com.okex.wallet'],
    install: 'https://www.okx.com/web3',
    legacy: () => window.okxwallet || (window.ethereum?.isOkxWallet ? window.ethereum : null) },
  { id: 'rabby', name: 'Rabby', icon: '🐰', rdns: ['io.rabby'],
    install: 'https://rabby.io/',
    legacy: () => (window.ethereum?.isRabby ? window.ethereum : null) },
];

// Collect EIP-6963 providers (announced by modern extensions; several can coexist without fighting over window.ethereum).
const announced = new Map(); // rdns -> { info, provider }
if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (e) => { const d = e.detail; if (d?.info?.rdns) announced.set(d.info.rdns, d); });
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}
export function detectWallets() {
  return WALLETS.map((w) => {
    const hit = w.rdns.map((r) => announced.get(r)).find(Boolean);
    const provider = hit?.provider || w.legacy();
    return { ...w, provider, detected: !!provider, iconUrl: hit?.info?.icon };
  });
}

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [handle, setHandle] = useState('');
  const [bnb, setBnb] = useState(0n);
  const [ore, setOre] = useState(0n);
  const [status, setStatus] = useState(null); // {kind:'info'|'ok'|'err', text, link}
  const [nonce, setNonce] = useState(0); // bump to refresh pages after a tx
  const [connectOpen, setConnectOpen] = useState(false);
  const [walletName, setWalletName] = useState('');
  const eip = useRef(null); // the EIP-1193 provider the user picked

  const refresh = useCallback(async (addr = account) => {
    if (!addr) return;
    const [h, b, o] = await Promise.all([
      contract('AgentIDRegistry').handleOf(addr).catch(() => ''),
      contract('ORE').runner.provider.getBalance(addr),
      contract('ORE').balanceOf(addr),
    ]);
    setHandle(h); setBnb(b); setOre(o); setNonce((n) => n + 1);
  }, [account]);

  const onAcc = useRef(null); const onChain = useRef(null);
  const connectWith = async (w) => {
    const inj = w?.provider;
    if (!inj) { setStatus({ kind: 'err', text: `${w?.name || 'Wallet'} not detected in this browser.` }); return false; }
    try {
      const prov = new ethers.BrowserProvider(inj);
      const accts = await prov.send('eth_requestAccounts', []);
      let cid = Number(await prov.send('eth_chainId', []));
      if (cid !== NET.chainId) {
        try { await prov.send('wallet_switchEthereumChain', [{ chainId: CHAIN_HEX }]); cid = NET.chainId; } catch (e) {
          await prov.send('wallet_addEthereumChain', [{ chainId: CHAIN_HEX, chainName: NET.label, rpcUrls: [NET.rpc],
            nativeCurrency: { name: NET.currency || 'BNB', symbol: NET.currency || 'BNB', decimals: 18 }, blockExplorerUrls: NET.explorer ? [NET.explorer] : [] }]);
          cid = NET.chainId;
        }
      }
      if (eip.current && eip.current !== inj && onAcc.current) {
        eip.current.removeListener?.('accountsChanged', onAcc.current); eip.current.removeListener?.('chainChanged', onChain.current);
      }
      eip.current = inj;
      onAcc.current = (a) => { if (!a.length) { setAccount(null); setSigner(null); } else connectWith(w); };
      onChain.current = (c) => setChainId(Number(c));
      inj.on?.('accountsChanged', onAcc.current); inj.on?.('chainChanged', onChain.current);
      const p2 = new ethers.BrowserProvider(inj);
      setSigner(withGasHeadroom(await p2.getSigner(accts[0])));
      setAccount(ethers.getAddress(accts[0])); setChainId(cid); setWalletName(w.name);
      setConnectOpen(false);
      try { localStorage.setItem('af-wallet', w.id); } catch (_) {}
      await refresh(ethers.getAddress(accts[0]));
      return true;
    } catch (e) { setStatus({ kind: 'err', text: `${w.name}: ${errMsg(e)}` }); return false; }
  };

  // After a page reload, silently restore the last wallet if it is still authorized (eth_accounts never opens a popup).
  useEffect(() => {
    let last = null; try { last = localStorage.getItem('af-wallet'); } catch (_) {}
    if (!last) return undefined;
    const t = setTimeout(async () => {
      const w = detectWallets().find((x) => x.id === last && x.detected);
      if (!w || account) return;
      try {
        const a = await w.provider.request({ method: 'eth_accounts' });
        if (!a?.length) return;
        const cid = Number(await w.provider.request({ method: 'eth_chainId' }));
        if (cid !== NET.chainId) return; // wrong network: let the user reconnect explicitly
        await connectWith(w);
      } catch (_) {}
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // Opens the wallet picker (Binance Wallet / MetaMask / OKX / Rabby).
  const connect = () => { window.dispatchEvent(new Event('eip6963:requestProvider')); setConnectOpen(true); };

  useEffect(() => () => {
    if (eip.current && onAcc.current) { eip.current.removeListener?.('accountsChanged', onAcc.current); eip.current.removeListener?.('chainChanged', onChain.current); }
  }, []);

  // Send a tx: label for the status line; fn(signer) returns a TransactionResponse.
  const send = async (label, fn) => {
    if (!signer) { connect(); return null; }
    try {
      // never sign on another network: the wallet may have been switched after connecting
      const cid = Number(await eip.current.request({ method: 'eth_chainId' }));
      if (cid !== NET.chainId) {
        setChainId(cid);
        try { await eip.current.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: CHAIN_HEX }] }); } catch (_) {
          setStatus({ kind: 'err', text: `${label}: your wallet is on another network. Switch it to ${NET.label} and try again.` }); return null;
        }
        setChainId(NET.chainId);
      }
      setStatus({ kind: 'info', text: `${label}: confirm in your wallet…` });
      // No popup after a while: it is usually hidden behind the browser window or waiting in the extension icon.
      const hint = setTimeout(() => setStatus({ kind: 'info', text: `${label}: still waiting for your wallet. If no window opened, click the ${walletName || 'wallet'} icon in your browser toolbar.` }), 12000);
      let tx;
      try { tx = await fn(signer); } finally { clearTimeout(hint); }
      setStatus({ kind: 'info', text: `${label}: waiting for confirmation…`, link: explorerTx(tx.hash) });
      let rc;
      try { rc = await tx.wait(); } catch (e) {
        // Mined but reverted (state changed after the dry run): replay it to get the contract's reason.
        if (e?.code !== 'CALL_EXCEPTION' || !e.receipt) throw e;
        try { await signer.provider.call({ from: tx.from, to: tx.to, data: tx.data, value: tx.value }); } catch (why) {
          if (errMsg(why) !== errMsg({})) throw why;
        }
        throw new Error('It failed on chain because something changed just before it was included (only gas was spent). Please try again.');
      }
      setStatus({ kind: 'ok', text: `${label}: confirmed (block ${rc.blockNumber})`, link: explorerTx(tx.hash) });
      await refresh();
      return rc;
    } catch (e) {
      setStatus({ kind: 'err', text: `${label}: ${errMsg(e)}` });
      return null;
    }
  };

  // Run any multi-step action (reads + txs) so a failure anywhere shows a plain message instead of doing nothing.
  const run = async (label, fn) => {
    if (!signer) { connect(); return null; }
    try { return await fn(); } catch (e) { setStatus({ kind: 'err', text: `${label}: ${errMsg(e)}` }); return null; }
  };

  const disconnect = () => { try { localStorage.removeItem('af-wallet'); } catch (_) {} setAccount(null); setSigner(null); setHandle(''); setWalletName(''); };
  const value = { account, signer, chainId, handle, hasAgentID: !!handle, bnb, ore, status, setStatus, nonce, connect, connectWith,
    connectOpen, setConnectOpen, walletName, disconnect, send, run, refresh };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWallet() { return useContext(Ctx); }
